import FormUploadTray from "../Forms/FormUploadTray";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAgencyAuth } from "../../context/AgencyAuthContext";
import Loading from "../common/Loading";
import OfficerInvite from "./OfficerInvite";
import OfficerTable from "./OfficerTable";
import Button from "../common/Button";
import Input from "../common/Input";
import { toastOptions } from "../common/toastOptions";
import { Cog8ToothIcon } from "@heroicons/react/24/solid";
import AgencyPreferences from "./AgencyPreferences";
import SSOConfiguration from "../SSOConfiguration";
import ChecklistManager from "./ChecklistManager";
import SnippetManager from "./SnippetManager";
import AgencyFormTemplateManager from "./AgencyFormTemplateManager";
import AgencyDocuments from "./AgencyDocuments/AgencyDocuments";
import DocumentUploadTray from "./AgencyDocuments/DocumentUploadTray";
import AdminManagement from "./AdminManagement";
import IntegrationManager from "./IntegrationManager";
import MFAConfiguration from "./MFAConfiguration";
import MFASettings from "../MFA/MFASettings";
import DataRetentionConfiguration from "./DataRetentionConfiguration";
import { InvestigationSeatsBar, useInvestigationSeats } from "./InvestigationSeats";
import { AgencyTutorialProvider } from "../../tutorials/TutorialProvider";
import { useTutorial } from "../../tutorials/useTutorial";

// Stats card component for dashboard
const StatsCard = ({
  title,
  value,
  subtitle,
  subtitleColor = "red",
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  subtitleColor?: "red" | "blue";
}) => {
  return (
    <div className="p-6 card border border-base-300 bg-base-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-base-content/70">{title}</p>
          <p className="text-2xl font-bold text-base-content mt-1">{value}</p>
          {subtitle && (
            <p
              className={`text-xs mt-1 ${
                subtitleColor === "blue" ? "text-blue-600 dark:text-blue-300" : "text-red-500"
              }`}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    "pk_live_51PydONG4bf6K0RLmdfEJzwpzVpDCNNdt0HjC5sn5QokyLJij6TflI306Xoi69SSqHORZ9cPnt3xw2leQx9rrYb0B00nka5yr7n",
);

// View type for dashboard navigation
type ActiveView =
  | "officers"
  | "preferences"
  | "sso"
  | "checklists"
  | "snippets"
  | "forms"
  | "documents"
  | "admins"
  | "integrations"
  | "mfa"
  | "retention";

function AgencyDashboardContent() {
  const { replayTutorial } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const { agency, setAgency } = useAgencyAuth();
  const [invitations, setInvitations] = useState<[]>([]);
  // Investigator seats live on the Officers tab: bought in the bar above the
  // table, assigned from each officer's role dropdown.
  const investigatorSeats = useInvestigationSeats();
  const videoSeats = useInvestigationSeats("video");
  const [seatsToPurchase, setSeatsToPurchase] = useState(1);

  // Derive activeView from the first path segment after /agency/dashboard/.
  // Sub-paths (e.g. /agency/dashboard/forms/<templateId>) keep the same activeView.
  const pathTail = location.pathname.split("/agency/dashboard/")[1] || "";
  const pathSegment = pathTail.split("/")[0];
  const activeView: ActiveView = (
    [
      "officers",
      "preferences",
      "sso",
      "checklists",
      "snippets",
      "forms",
      "documents",
      "admins",
      "integrations",
      "mfa",
      "retention",
    ].includes(pathSegment)
      ? pathSegment
      : "officers"
  ) as ActiveView;
  const [totalCases, setTotalCases] = useState<number>(0);
  const [swornOfficersInput, setSwornOfficersInput] = useState("");
  const [swornOfficersError, setSwornOfficersError] = useState("");
  const [savingSwornOfficers, setSavingSwornOfficers] = useState(false);
  const showSwornOfficersModal = agency?.sworn_officers == null;

  // Define price per seat - this would ideally come from your backend or environment
  const PRICE_PER_SEAT = 359.88; // $25 per seat per month

  // Calculate total price in real time as seats change
  const totalPrice = useMemo(() => {
    return (seatsToPurchase * PRICE_PER_SEAT)
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }, [seatsToPurchase]);

  const fetchInvitations = async () => {
    try {
      const { data } = await axios.get("/api/agency/invitations");

      // Clean and deduplicate the data
      const uniqueInvitations = data.reduce((acc: any[], curr: any) => {
        const existingIndex = acc.findIndex(
          (item) => item.email === curr.email,
        );
        if (existingIndex === -1) {
          acc.push(curr);
        } else if (curr.createdAt && !acc[existingIndex].createdAt) {
          acc[existingIndex] = curr;
        }
        return acc;
      }, []);

      setInvitations(uniqueInvitations);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching invitations:", error);
      setIsLoading(false);
    }
  };

  const fetchAgencyStats = async () => {
    try {
      const { data } = await axios.get("/api/agency/stats");
      setTotalCases(data.totalCases);
    } catch (error) {
      console.error("Error fetching agency stats:", error);
    }
  };

  // Redirect to default tab if no sub-path
  useEffect(() => {
    if (!pathSegment) {
      navigate("/agency/dashboard/officers", { replace: true });
    }
  }, [pathSegment, navigate]);

  useEffect(() => {
    if (agency) {
      fetchInvitations();
      fetchAgencyStats();
    }
  }, [agency]);

  // Back from Stripe after buying investigator seats (see the add-on
  // checkout's success_url). The webhook may land a beat after the
  // redirect, so refresh the seat counts once more shortly after.
  useEffect(() => {
    if (searchParams.get("seats") !== "purchased") return;
    toast.success("Investigator seats added");
    const t = setTimeout(() => {
      investigatorSeats.reload();
      videoSeats.reload();
    }, 3000);
    navigate("/agency/dashboard/officers", { replace: true });
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleInvite = async (emails: string[], role: string = "officer") => {
    try {
      // Immediately refresh the invitations list after successful invite
      const { data } = await axios.post("/api/agency/invite", { emails, role });

      // Show success for successful invites
      data.results.forEach((result) => {
        if (result.status === "existing") {
          toast.error(`${result.email} already belongs to an agency`);
        }
      });
      fetchInvitations();
    } catch (error) {
      console.error("Error sending invitations:", error);
    }
  };

  const handlePurchaseSeats = async () => {
    try {
      const { data } = await axios.post("/api/agency/purchase-seats", {
        seats: seatsToPurchase,
      });

      const stripe = await stripePromise;

      if (stripe) {
        const { error } = await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });

        if (error) {
          console.error("Error:", error);
        }
      }
    } catch (error) {
      console.error("Error purchasing seats:", error);
    }
  };

  // Function to manage subscription if needed in the future
  /*
  const handleManageSubscription = async () => {
    try {
      const response = await axios.post(
        "/api/agency/portal",
        {},
        {
          withCredentials: true,
        }
      );

      const { url } = response.data;
      window.location.href = url;
    } catch (error) {
      console.error("Error accessing subscription portal:", error);
    }
  };
  */

  const handleLogout = async () => {
    try {
      await axios.post("/api/auth/logout");
      navigate("/agency/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleSwornOfficersSubmit = async () => {
    const value = parseInt(swornOfficersInput);
    if (!swornOfficersInput || isNaN(value) || value < 0) {
      setSwornOfficersError("Please enter a valid number");
      return;
    }
    setSavingSwornOfficers(true);
    try {
      const { data } = await axios.patch("/api/agency/sworn-officers", {
        sworn_officers: value,
      });
      setAgency(data.agency);
    } catch (error) {
      console.error("Error updating sworn officers:", error);
      setSwornOfficersError("Failed to save. Please try again.");
    } finally {
      setSavingSwornOfficers(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  // Trial seats (granted via a signup promo code) only count while the
  // trial hasn't expired — expiration is enforced here at read time.
  const trialExpiresAt = agency?.trial_expires_at
    ? new Date(agency.trial_expires_at)
    : null;
  const trialActive =
    (agency?.trial_seats || 0) > 0 &&
    trialExpiresAt !== null &&
    trialExpiresAt > new Date();
  const trialSeats = trialActive ? agency?.trial_seats || 0 : 0;
  const trialDaysLeft = trialExpiresAt
    ? Math.max(
        0,
        Math.ceil(
          (trialExpiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        ),
      )
    : 0;

  const hasSeats = (agency?.max_users || 0) + trialSeats > 0;

  // Format subscription status
  const formatStatus = (status: string) => {
    switch (status) {
      case "trialing":
        return "Trialing";
      case "active":
        return "Active";
      case "incomplete_expired":
        return "Expired";
      default:
        return status;
    }
  };

  // Format expiration date
  const formatExpirationDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const daysUntilExpiration = Math.ceil(
      (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    if (daysUntilExpiration < 0) {
      return `${formattedDate} (Expired)`;
    } else if (daysUntilExpiration <= 7) {
      return `${formattedDate} (${daysUntilExpiration} days left)`;
    }
    return formattedDate;
  };

  // Calculate statistics for dashboard cards
  const totalSeats = (agency?.max_users || 0) + trialSeats;
  // Count active seats as users who have accepted (these are actual users with agency_id set)
  const activeSeats = invitations.filter(
    (inv: any) => inv.accepted === true,
  ).length;
  // Count pending invites (these are invitations that haven't been accepted yet)
  const allPendingInvites = invitations.filter(
    (inv: any) => inv.accepted === false,
  );
  const expiredInvites = allPendingInvites.filter(
    (inv: any) => inv.expiresAt && new Date(inv.expiresAt) < new Date(),
  ).length;
  const pendingInvites = allPendingInvites.length;
  // Available seats should be total seats minus active seats (not all invitations)
  const availableSeats = Math.max(0, totalSeats - activeSeats - pendingInvites);

  return (
    <>
      <div className="min-h-screen bg-base-200">
        <div className="p-8 max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-base-content flex items-center gap-1">
                Agency Settings:{" "}
                <span className="text-blue-700 dark:text-blue-300">{agency?.name}</span>
                <div className="dropdown dropdown-bottom">
                  <div>
                    <Cog8ToothIcon
                      tabIndex={0}
                      className="mt-1 size-8 text-base-content/60 opacity-50 hover:opacity-80 transition-opacity cursor-pointer"
                    />
                  </div>
                  <ul
                    tabIndex={0}
                    className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-52 p-2 shadow mt-2"
                  >
                    <li>
                      <button
                        onClick={handleLogout}
                        className="hover:bg-gray-100 dark:hover:bg-base-200 w-full text-left"
                      >
                        Log out
                      </button>
                    </li>
                  </ul>
                </div>
              </h1>
              <p className="mt-2 text-base-content/60 font-semibold">
                Manage your agency settings
                <button
                  type="button"
                  data-tour="agency-tour-replay"
                  onClick={() => replayTutorial("agency-invite-officers")}
                  className="ml-3 text-sm font-medium text-blue-600 dark:text-blue-300 hover:underline"
                >
                  Take the tour
                </button>
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          {trialActive && (
            <div className="mb-8 p-6 card border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40" data-tour="agency-trial-banner">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-64 max-w-2xl">
                  <h2 className="text-xl font-bold text-blue-900 dark:text-blue-200">
                    Evaluation Period
                  </h2>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Welcome to Police Narratives AI. Your department has been
                    provisioned {trialSeats}{" "}
                    {trialSeats === 1 ? "seat" : "seats"}
                    {invitations.length < trialSeats / 3
                      ? " — invite your officers below to begin."
                      : "."}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    Evaluation ends
                  </p>
                  <p className="text-lg font-semibold text-blue-900 dark:text-blue-200">
                    {trialExpiresAt?.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}{" "}
                    ({trialDaysLeft} {trialDaysLeft === 1 ? "day" : "days"}{" "}
                    left)
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <StatsCard
              title="Total Seats"
              value={totalSeats}
              subtitle={
                trialActive
                  ? `includes ${trialSeats} evaluation seats`
                  : undefined
              }
              subtitleColor="blue"
            />
            <StatsCard title="Active Seats" value={activeSeats} />
            <StatsCard title="Available Seats" value={availableSeats} />
            <StatsCard
              title="Pending Invites"
              value={pendingInvites}
              subtitle={
                expiredInvites > 0 ? `${expiredInvites} expired` : undefined
              }
            />
            <StatsCard title="Reports Written" value={totalCases} />
          </div>

          {/* Subscription Information */}
          {(agency as any)?.subscription && (
            <div className="p-6 card border border-base-300 bg-base-100 mb-8">
              <h2 className="text-xl font-bold text-base-content mb-4">
                Subscription Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-base-content/70 mb-1">
                    Status
                  </p>
                  <span
                    className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                      (agency as any).subscription.status === "active"
                        ? "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-400"
                        : (agency as any).subscription.status === "trialing"
                          ? "bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300"
                          : "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-400"
                    }`}
                  >
                    {formatStatus((agency as any).subscription.status)}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-base-content/70 mb-1">
                    Expiration Date
                  </p>
                  <p className="text-lg font-semibold text-base-content">
                    {formatExpirationDate(
                      (agency as any).subscription.current_period_end,
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="mb-8 border-b border-base-300 overflow-x-auto">
            <nav className="flex gap-0 -mb-px min-w-max">
              {(
                [
                  { key: "officers", label: "Officers" },
                  { key: "preferences", label: "Report Preferences" },
                  { key: "checklists", label: "Checklists" },
                  { key: "snippets", label: "Snippets" },
                  { key: "forms", label: "Form Templates" },
                  { key: "documents", label: "Documents" },
                  { key: "admins", label: "Admins" },
                  { key: "integrations", label: "Integrations" },
                  { key: "mfa", label: "MFA" },
                  { key: "retention", label: "Data Retention" },
                  { key: "sso", label: "Single Sign-On" },
                ] as { key: ActiveView; label: string }[]
              ).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => navigate(`/agency/dashboard/${tab.key}`)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeView === tab.key
                      ? "border-blue-500 border-b-[3px] text-blue-600 dark:text-blue-300 font-semibold"
                      : "border-transparent text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80 hover:border-gray-300 dark:hover:border-base-content/30"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {!hasSeats ? (
            <div className="p-6 card border border-base-300 bg-base-100">
              <h2 className="card-title">Manage Seats</h2>
              <p className="text-gray-600 dark:text-base-content/70 mb-4">
                Your agency doesn't have any seats available. Purchase seats to
                start inviting officers to your agency.
              </p>

              <div className="bg-base-200 p-8 relative rounded-md">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="seats"
                      className="text-gray-700 dark:text-base-content/80 font-medium"
                    >
                      Number of seats:
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setSeatsToPurchase(Math.max(1, seatsToPurchase - 1))
                        }
                        className="px-3 py-1 border rounded bg-base-100 hover:bg-gray-100 dark:hover:bg-base-200"
                      >
                        -
                      </button>
                      <input
                        id="seats"
                        type="number"
                        min="1"
                        value={seatsToPurchase}
                        onChange={(e) =>
                          setSeatsToPurchase(
                            Math.max(1, parseInt(e.target.value) || 1),
                          )
                        }
                        className="border rounded px-3 py-2 w-24 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        onClick={() => setSeatsToPurchase(seatsToPurchase + 1)}
                        className="px-3 py-1 border rounded bg-base-100 hover:bg-gray-100 dark:hover:bg-base-200"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="bg-base-100 p-4 border border-base-300 rounded-md">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600 dark:text-base-content/70">
                        {seatsToPurchase}{" "}
                        {seatsToPurchase === 1 ? "seat" : "seats"} × $
                        {PRICE_PER_SEAT} / year
                      </span>
                      <span className="text-gray-600 dark:text-base-content/70">
                        ${totalPrice} / year
                      </span>
                    </div>
                    <div className="border-t pt-2 mt-2 flex justify-between items-center">
                      <span className="font-medium">Total</span>
                      <span className="font-bold text-lg">
                        ${totalPrice} / year
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handlePurchaseSeats}
                    variant="primary"
                    className="w-full mt-2"
                  >
                    Purchase Seats
                  </Button>
                </div>
              </div>
              <p className="text-base-content/60 text-sm mt-4 text-center">
                Each seat allows one officer to access the platform. Please
                contact{" "}
                <a href="mailto:support@policenarratives.ai">
                  support@policenarratives.ai
                </a>{" "}
                if you have any questions.
              </p>
            </div>
          ) : (
            <>
              {activeView === "officers" && (
                <>
                  <div className="mt-8">
                    <OfficerInvite
                      maxOfficers={totalSeats}
                      currentOfficers={activeSeats}
                      availableSeats={availableSeats}
                      onInvite={handleInvite}
                    />
                  </div>
                  {/* Only anchored when investigator seats were granted, so
                      the tour's investigator step is skipped otherwise. */}
                  {(investigatorSeats.data?.seatsPurchased ?? 0) > 0 ? (
                    <div data-tour="agency-investigator-seats">
                      <InvestigationSeatsBar data={investigatorSeats.data} />
                    </div>
                  ) : (
                    <InvestigationSeatsBar data={investigatorSeats.data} />
                  )}
                  <InvestigationSeatsBar data={videoSeats.data} addon="video" />
                  <OfficerTable
                    officers={invitations}
                    onUpdate={fetchInvitations}
                    seats={investigatorSeats.data}
                    onSeatsChanged={investigatorSeats.reload}
                    videoSeats={videoSeats.data}
                    onVideoSeatsChanged={videoSeats.reload}
                  />
                </>
              )}
              {activeView === "preferences" && <AgencyPreferences />}
              {activeView === "checklists" && <ChecklistManager />}
              {activeView === "snippets" && <SnippetManager />}
              {activeView === "forms" && <AgencyFormTemplateManager />}
              {activeView === "documents" && <AgencyDocuments />}
              {activeView === "admins" && <AdminManagement />}
              {activeView === "integrations" && <IntegrationManager />}
              {activeView === "sso" && <SSOConfiguration />}
              {activeView === "mfa" && (
                <>
                  <MFAConfiguration />
                  {/* Agency-wide policy above; the admin's own second factors
                      below. Without this an admin enrolled in email only had
                      nowhere to add an authenticator app — the same dead end
                      officers hit on /settings. All /api/mfa/* endpoints branch
                      on isAdmin, so the component works unchanged here. */}
                  <div className="mt-8 pt-8 border-t border-base-300">
                    <h3 className="text-lg font-bold text-base-content mb-1">Your sign-in methods</h3>
                    <p className="text-sm text-base-content/60 mb-6">The second factors on your own admin account.</p>
                    <MFASettings />
                  </div>
                </>
              )}
              {activeView === "retention" && <DataRetentionConfiguration />}
            </>
          )}
        </div>
      </div>
      {/* Form template uploads + AI field reading; survives tab changes. */}
      <FormUploadTray />
      {/* Policy document uploads + processing stages; survives tab changes. */}
      <DocumentUploadTray />
      <Toaster position="bottom-center" toastOptions={toastOptions} />

      {/* Sworn Officers Modal */}
      <dialog className={`modal ${showSwornOfficersModal ? "modal-open" : ""}`}>
        <div className="modal-box max-w-sm">
          <h3 className="font-bold text-lg mt-2">Agency Information</h3>
          <p className="text-sm text-base-content/60 mt-2 mb-4">
            Please enter the number of sworn officers in your agency to
            continue.
          </p>
          <div className="mb-4">
            <Input
              type="number"
              value={swornOfficersInput}
              placeholder="# of sworn officers"
              min="0"
              onChange={(e) => {
                setSwornOfficersError("");
                setSwornOfficersInput(e.target.value.replace(/\D/g, ""));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
                if (e.key === "Enter") {
                  handleSwornOfficersSubmit();
                }
              }}
              className={`input w-full !max-w-full ${
                swornOfficersError && "border-red-500"
              }`}
            />
            {swornOfficersError && (
              <p className="mt-1 text-xs text-red-500">{swornOfficersError}</p>
            )}
          </div>
          <Button
            onClick={handleSwornOfficersSubmit}
            variant="primary"
            className="w-full"
            disabled={savingSwornOfficers || !swornOfficersInput}
          >
            {savingSwornOfficers ? "Saving..." : "Continue"}
          </Button>
        </div>
        <div className="modal-backdrop" />
      </dialog>
    </>
  );
}

// The agency portal's guided tours (Tutorials/tours with audience "agency")
// run from here; the provider needs the agency session, so it wraps the page.
function Dashboard() {
  return (
    <AgencyTutorialProvider>
      <AgencyDashboardContent />
    </AgencyTutorialProvider>
  );
}

export default Dashboard;
